import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Spinner } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import {
  googleBusinessApi,
  googleBusinessKeys,
  type GbpRecommendation,
  type GbpReview,
} from "./googleBusinessApi";

/**
 * Read + write surfaces for one GBP location (Phase 2 read, Phase 3 write): computed recommendations,
 * the reviews list with reply/delete, and a one-field phone sync (our venue's phone → the listing).
 * Rendered lazily when the owner expands a location. No Google token ever reaches the SPA.
 */
export function GoogleBusinessLocationDetail({ locationId }: { locationId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const recommendations = useQuery({
    queryKey: googleBusinessKeys.recommendations(locationId),
    queryFn: () => googleBusinessApi.recommendations(locationId),
  });
  const reviews = useQuery({
    queryKey: googleBusinessKeys.reviews(locationId),
    queryFn: () => googleBusinessApi.reviews(locationId),
  });
  const phoneSync = useQuery({
    queryKey: googleBusinessKeys.phoneSync(locationId),
    queryFn: () => googleBusinessApi.phoneSync(locationId),
  });

  const afterReviewWrite = () => {
    void qc.invalidateQueries({ queryKey: googleBusinessKeys.reviews(locationId) });
    void qc.invalidateQueries({ queryKey: googleBusinessKeys.recommendations(locationId) });
  };

  const reply = useMutation({
    mutationFn: ({ reviewId, comment }: { reviewId: string; comment: string }) =>
      googleBusinessApi.replyToReview(locationId, reviewId, comment),
    onSuccess: afterReviewWrite,
  });
  const deleteReply = useMutation({
    mutationFn: (reviewId: string) => googleBusinessApi.deleteReviewReply(locationId, reviewId),
    onSuccess: afterReviewWrite,
  });
  const pushPhone = useMutation({
    mutationFn: () => googleBusinessApi.pushPhone(locationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: googleBusinessKeys.phoneSync(locationId) });
      void qc.invalidateQueries({ queryKey: googleBusinessKeys.recommendations(locationId) });
    },
  });

  const loading = recommendations.isLoading || reviews.isLoading;

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "4",
        borderTopWidth: "1px",
        borderColor: "border.default",
        mt: "2",
        pt: "3",
      })}
    >
      {loading && <Spinner aria-label={t("googleBusiness.loading")} />}

      {phoneSync.data && !phoneSync.data.inSync && phoneSync.data.ourPhone && (
        <section
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "2",
            bg: "bg.muted",
            rounded: "md",
            p: "3",
          })}
        >
          <h4 className={css({ fontSize: "sm", fontWeight: "semibold", color: "fg.default" })}>
            {t("googleBusiness.phoneSync.heading")}
          </h4>
          <p className={css({ fontSize: "sm", color: "fg.muted" })}>
            {t("googleBusiness.phoneSync.diff", {
              our: phoneSync.data.ourPhone,
              google: phoneSync.data.googlePhone ?? "—",
            })}
          </p>
          <Button
            size="xs"
            variant="outline"
            loading={pushPhone.isPending}
            onClick={() => pushPhone.mutate()}
          >
            {t("googleBusiness.phoneSync.push")}
          </Button>
        </section>
      )}

      {recommendations.data && (
        <section className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
          <h4 className={css({ fontSize: "sm", fontWeight: "semibold", color: "fg.default" })}>
            {t("googleBusiness.rec.heading")}
          </h4>
          {recommendations.data.length === 0 ? (
            <p className={css({ fontSize: "sm", color: "fg.muted" })}>
              {t("googleBusiness.rec.none")}
            </p>
          ) : (
            <ul className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
              {recommendations.data.map((rec) => (
                <li
                  key={rec.kind}
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    gap: "2",
                    fontSize: "sm",
                  })}
                >
                  <Badge colorPalette={severityPalette(rec.severity)}>{rec.severity}</Badge>
                  <span className={css({ color: "fg.default" })}>{recLabel(t, rec)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {recommendations.isError && (
        <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
          {t("googleBusiness.detailFailed")}
        </p>
      )}

      {reviews.isError && (
        <section className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
          <h4 className={css({ fontSize: "sm", fontWeight: "semibold", color: "fg.default" })}>
            {t("googleBusiness.reviews.heading")}
          </h4>
          <p className={css({ fontSize: "sm", color: "fg.muted" })}>
            {t("googleBusiness.reviews.unavailable")}
          </p>
        </section>
      )}

      {reviews.data && (
        <section className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
          <h4 className={css({ fontSize: "sm", fontWeight: "semibold", color: "fg.default" })}>
            {t("googleBusiness.reviews.heading")}
            {typeof reviews.data.averageRating === "number" && (
              <span className={css({ ml: "2", fontWeight: "normal", color: "fg.muted" })}>
                {t("googleBusiness.reviews.summary", {
                  avg: reviews.data.averageRating.toFixed(1),
                  count: reviews.data.totalReviewCount,
                })}
              </span>
            )}
          </h4>
          {reviews.data.reviews.length === 0 ? (
            <p className={css({ fontSize: "sm", color: "fg.muted" })}>
              {t("googleBusiness.reviews.none")}
            </p>
          ) : (
            <ul className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
              {reviews.data.reviews.map((r) => (
                <ReviewItem
                  key={r.reviewId}
                  review={r}
                  busy={reply.isPending || deleteReply.isPending}
                  onReply={(comment) => reply.mutate({ reviewId: r.reviewId, comment })}
                  onDelete={() => deleteReply.mutate(r.reviewId)}
                />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function ReviewItem({
  review,
  busy,
  onReply,
  onDelete,
}: {
  review: GbpReview;
  busy: boolean;
  onReply: (comment: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(review.replyComment ?? "");

  return (
    <li
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "2",
        borderWidth: "1px",
        borderColor: "border.default",
        rounded: "md",
        px: "3",
        py: "2",
      })}
    >
      <div className={css({ display: "flex", alignItems: "center", gap: "2" })}>
        <span className={css({ fontSize: "sm", fontWeight: "medium" })}>
          {"★".repeat(review.starRating)}
          {"☆".repeat(Math.max(0, 5 - review.starRating))}
        </span>
        <span className={css({ fontSize: "sm", color: "fg.muted" })}>{review.reviewerName}</span>
        <Badge colorPalette={review.answered ? "green" : "gray"}>
          {review.answered
            ? t("googleBusiness.reviews.answered")
            : t("googleBusiness.reviews.unanswered")}
        </Badge>
      </div>
      {review.comment && (
        <p className={css({ fontSize: "sm", color: "fg.default" })}>{review.comment}</p>
      )}

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={t("googleBusiness.reply.placeholder")}
        rows={2}
        className={css({
          w: "full",
          px: "2",
          py: "1",
          rounded: "md",
          borderWidth: "1px",
          borderColor: "border.default",
          bg: "bg.default",
          fontSize: "sm",
        })}
      />
      <div className={css({ display: "flex", gap: "2" })}>
        <Button
          size="xs"
          disabled={busy || draft.trim() === ""}
          onClick={() => onReply(draft.trim())}
        >
          {review.answered ? t("googleBusiness.reply.update") : t("googleBusiness.reply.submit")}
        </Button>
        {review.answered && (
          <Button size="xs" variant="ghost" disabled={busy} onClick={onDelete}>
            {t("googleBusiness.reply.delete")}
          </Button>
        )}
      </div>
    </li>
  );
}

function severityPalette(severity: string): string {
  if (severity === "WARNING") return "orange";
  if (severity === "SUGGESTION") return "blue";
  return "gray";
}

function recLabel(
  t: (k: string, o?: Record<string, unknown>) => string,
  rec: GbpRecommendation,
): string {
  return t(`googleBusiness.rec.${rec.kind}`, { count: rec.count ?? 0 });
}
